import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/GoogleButton";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Console provisioned — welcome aboard");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">Start free</h1>
      <p className="mt-2 text-sm text-muted">First 10 companies get 3 months on us. No credit card required.</p>

      <div className="mt-8"><GoogleButton /></div>

      <div className="my-6 flex items-center gap-4">
        <div className="h-px flex-1 bg-line" />
        <span className="text-xs text-muted font-mono">OR</span>
        <div className="h-px flex-1 bg-line" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="name" className="text-ink">Full name</Label>
            <Input id="name" required value={form.name} onChange={upd("name")} data-testid="signup-name"
              placeholder="Jordan Lee" className="mt-1.5 bg-[var(--surface-2)] border-line text-ink" />
          </div>
          <div>
            <Label htmlFor="company" className="text-ink">Company</Label>
            <Input id="company" value={form.company} onChange={upd("company")} data-testid="signup-company"
              placeholder="Acme Inc" className="mt-1.5 bg-[var(--surface-2)] border-line text-ink" />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-ink">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={upd("email")} data-testid="signup-email"
            placeholder="you@company.com" className="mt-1.5 bg-[var(--surface-2)] border-line text-ink" />
        </div>
        <div>
          <Label htmlFor="password" className="text-ink">Password</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={upd("password")}
            data-testid="signup-password" placeholder="At least 6 characters"
            className="mt-1.5 bg-[var(--surface-2)] border-line text-ink" />
        </div>
        <Button type="submit" disabled={loading} data-testid="signup-submit"
          className="w-full bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold h-11">
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Create Console"}
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted text-center">
        Already have an account?{" "}
        <Link to="/login" className="text-cyber font-semibold hover:underline" data-testid="link-login">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
